import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react';
import EasterEggImage from 'assets/images/home/easter-egg.png';
import PrizeCard from './PrizeModal';
import { ReactComponent as MemberIcon } from 'assets/images/lucky-draw/member.svg';
import AvatarIcon from 'assets/images/lucky-draw/avatar.png';
import { getOnePieceServerApi } from 'gql-ops';
import { getCurrentChainId } from 'gql';
import user from 'stores/user';
import luckyDrawStore from 'stores/luckyDrawStore';
import { PrepareDrawValue } from 'gql-ops/graph/__generated__/request';
import { useLuckyDrawContract } from 'hooks/useLuckyDrawContract';
import { useWalletClient, useSignTypedData } from 'wagmi';
import LuckyDrawABI from 'contracts/luckyDraw.json';
import { LUCKY_DRAW_CONTRACT_ADDRESS } from 'constant';
import { useContract } from 'hooks/useContract';
import useSend from 'hooks/useSend';
import { ethers } from 'ethers';
import ErrorIcon from '@mui/icons-material/Error';
import { TYPE } from 'theme';
// import PrizeProbabilityBar from './PrizeProbabilityBar';
import BigNumber from 'bignumber.js';
import TooltipInfo from 'components/TooltipInfo';
import Row from 'components/Row';

const LuckyDrawPoolContainer = styled.div`
    background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, #000 32.22%),
        radial-gradient(circle at top left, var(--Background-Brand-2-70, rgba(247, 147, 26, 0.7)) 0%, transparent 30%),
        radial-gradient(circle at top right, var(--Background-Brand-1, #6a0dad) 0%, transparent 30%),
        var(--Background-Primary, #000);
    padding: 40px;
    border-radius: 24px;
    position: relative;
    overflow: hidden;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;

    @media (max-width: 768px) {
        padding: 20px;
        border-radius: 16px;
        margin-bottom: 20px;
    }

    @media (max-width: 480px) {
        padding: 15px;
        border-radius: 12px;
        margin-bottom: 15px;
    }
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 40px;

    @media (max-width: 768px) {
        margin-bottom: 20px;
        flex-direction: column;
        gap: 15px;
        align-items: flex-start;
    }

    @media (max-width: 480px) {
        margin-bottom: 15px;
        gap: 10px;
    }
`;

const RefreshButton = styled.button`
    background: transparent;
    border: 1px solid rgba(247, 147, 26, 1);
    border-radius: 8px;
    color: white;
    padding: 8px 16px;
    font-size: 14px;
    transition: all 0.3s ease;

    &:hover {
        background: rgba(106, 13, 173, 0.2);
    }
`;

const MainContent = styled.div`
    display: grid;
    grid-template-columns: 8fr 7fr;
    gap: 40px;
    @media (max-width: 768px) {
        grid-template-columns: 1fr;
        gap: 20px;
    }
`;

const LeftPanel = styled.div`
    background: linear-gradient(135deg, rgba(106, 13, 173, 0.1) 0%, rgba(247, 147, 26, 0.1) 100%);
    border-radius: 32px;
    padding: 60px 40px;
    position: relative;
    overflow: hidden;
    box-shadow: 0px 0px 40px 0px rgba(106, 13, 173, 1) inset;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(135deg, rgba(106, 13, 173, 0.05) 0%, rgba(247, 147, 26, 0.05) 100%);
        border-radius: 14px;
    }

    @media (max-width: 768px) {
        padding: 30px 20px;
        border-radius: 20px;
    }

    @media (max-width: 480px) {
        padding: 20px 15px;
        border-radius: 16px;
    }
`;

const RightPanel = styled.div`
    background: linear-gradient(135deg, rgba(106, 13, 173, 0.1) 0%, rgba(247, 147, 26, 0.1) 100%);
    border-radius: 16px;
    padding: 40px;
    position: relative;
    overflow: hidden;
    box-shadow: 0px 0px 40px 0px rgba(106, 13, 173, 1) inset;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(135deg, rgba(106, 13, 173, 0.05) 0%, rgba(247, 147, 26, 0.05) 100%);
        border-radius: 14px;
    }

    @media (max-width: 768px) {
        padding: 30px 20px;
        border-radius: 12px;
    }

    @media (max-width: 480px) {
        padding: 20px 15px;
        border-radius: 10px;
    }
`;

const GiftGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    position: relative;
    z-index: 1;

    @media (max-width: 768px) {
        gap: 12px;
    }

    @media (max-width: 480px) {
        gap: 8px;
    }
`;

const GiftBox = styled.div`
    aspect-ratio: 1;
    position: relative;
    cursor: pointer;
    transition: all 0.3s ease;
    border-radius: 16px;
    overflow: hidden;
    /* 外部渐变背景 */
    background: linear-gradient(180deg, #fcb72a 0%, #6a0dad 100%);
    &:hover {
        transform: scale(1.05);
    }

    @media (max-width: 768px) {
        border-radius: 12px;
    }

    @media (max-width: 480px) {
        border-radius: 8px;
    }
`;

const InnerBox = styled.div<{
    isHighlighted?: boolean;
    isWinning?: boolean;
}>`
    width: calc(100% - 12px);
    height: calc(100% - 12px);
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 6px;
    left: 6px;
    background: ${({ isHighlighted, isWinning }) =>
        isHighlighted || isWinning ? 'rgba(247, 147, 26, 1)' : 'rgba(110, 19, 174, 1)'};
    border: 1px solid;
    border-image-source: linear-gradient(
        180deg,
        rgba(255, 255, 255, 0.6) 0%,
        rgba(255, 255, 255, 0.2) 50%,
        rgba(255, 255, 255, 0.6) 100%
    );

    border: 1px solid #999;
    border-radius: 16px;

    /* 礼品盒图标 */
    .gift-icon {
        position: relative;
        z-index: 2;
        width: 50%;
        height: 50%;
        background-image: url(${EasterEggImage});
        background-size: contain;
        background-repeat: no-repeat;
        background-position: center;
        filter: ${({ isWinning, isHighlighted }) => {
            if (isWinning) return 'brightness(1.3) drop-shadow(0 0 12px rgba(255, 215, 0, 0.8))';
            if (isHighlighted) return 'brightness(1.2) drop-shadow(0 0 10px rgba(247, 147, 26, 0.6))';
            return 'brightness(1.1) drop-shadow(0 0 8px rgba(210, 125, 255, 0.5))';
        }};
    }
`;

const CountdownSection = styled.div`
    text-align: center;
    margin-bottom: 30px;
    position: relative;
    z-index: 1;
`;

const CountdownTimer = styled.div`
    font-size: 24px;
    font-weight: 500;
    color: #f7931a;
    font-family: 'Courier New', monospace;
    text-shadow: 0 0 10px rgba(247, 147, 26, 0.5);
    margin-bottom: 8px;
`;

const EndTimeLabel = styled.div`
    font-size: 14px;
    color: white;
`;

const TabContainer = styled.div`
    display: flex;
    justify-content: center;
    gap: 8px;
    margin-bottom: 20px;
    position: relative;
    z-index: 1;
`;

const Tab = styled.button<{ isActive?: boolean; disabled?: boolean }>`
    background: ${({ isActive }) => (isActive ? 'rgba(50, 50, 50, 1)' : 'transparent')};
    border: none;
    border-radius: 16px;
    color: ${({ isActive, disabled }) => (disabled ? '#666' : isActive ? 'rgba(247, 147, 26, 1)' : '#999')};
    padding: 0 16px;
    height: 34px;
    font-size: 14px;
    font-weight: 500;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    transition: all 0.3s ease;
    position: relative;
    opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
    &:hover {
        background: ${({ disabled }) => (disabled ? 'transparent' : 'rgba(255, 255, 255, 0.2)')};
    }
`;

const ComingSoonBadge = styled.div`
    position: absolute;
    top: -6px;
    right: -6px;
    background: rgba(247, 147, 26, 1);
    color: #000;
    padding: 3px 6px;
    font-size: 9px;
    border-radius: 0 8px;
    font-weight: 600;
    white-space: nowrap;
    z-index: 2;
    transform: translate(50%, -50%);
`;

const DrawQuantityContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    position: relative;
    justify-content: center;
    z-index: 1;
    margin-top: 40px;
`;

const QuantityButton = styled.button`
    width: 40px;
    height: 40px;
    background: #6a0dad;
    border: none;
    border-radius: 8px;
    color: white;
    font-size: 18px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    line-height: 1;

    &:hover {
        background: #5a0c5a;
        transform: scale(1.05);
    }
`;

const QuantityDisplay = styled.input`
    width: 88px;
    height: 38px;
    background: rgba(50, 50, 50, 1);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 18px;
    font-weight: 700;
    text-align: center;
    outline: none;

    &:focus {
        border-color: #6a0dad;
        box-shadow: 0 0 0 2px rgba(106, 13, 173, 0.2);
    }

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    &[type='number'] {
        -moz-appearance: textfield;
    }
`;

const DrawInfo = styled.div`
    margin-bottom: 40px;
    position: relative;
    z-index: 1;
`;

const DrawInfoRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
    color: rgba(153, 153, 153, 1);
    font-size: 12px;
`;

const StartDrawingButton = styled.button<{ disabled?: boolean }>`
    width: 100%;
    height: 50px;
    background: ${({ disabled }) =>
        disabled ? 'rgba(100, 100, 100, 0.3)' : 'linear-gradient(135deg, #f7931a 0%, #6a0dad 100%)'};
    border: none;
    border-radius: 50px;
    color: ${({ disabled }) => (disabled ? '#666' : 'white')};
    font-size: 16px;
    font-weight: 700;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    transition: all 0.3s ease;
    margin-bottom: 20px;
    position: relative;
    z-index: 1;
    opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};

    &:hover {
        transform: ${({ disabled }) => (disabled ? 'none' : 'translateY(-2px)')};
        box-shadow: ${({ disabled }) => (disabled ? 'none' : '0 8px 20px rgba(247, 147, 26, 0.3)')};
    }

    @media (max-width: 768px) {
        height: 45px;
        font-size: 14px;
        margin-bottom: 15px;
    }

    @media (max-width: 480px) {
        height: 40px;
        font-size: 13px;
        margin-bottom: 10px;
    }
`;

const StyledErrorIcon = styled(ErrorIcon)`
    width: 16px;
    height: 16px;
    cursor: pointer;
    color: rgba(88, 88, 88, 1);
`;

const ParticipantsSection = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    position: relative;
    z-index: 1;
`;

const ParticipantCount = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    color: white;
    font-size: 14px;
`;

const MAX_DRAWS_PER_TIME = 10;

function LuckyDrawPool() {
    const [activeTab, setActiveTab] = useState('Points');
    const [drawQuantity, setDrawQuantity] = useState(0);
    const [quantityInputValue, setQuantityInputValue] = useState<string>('0');
    const [countdown, setCountdown] = useState('00:00:00');
    const { signTypedDataAsync } = useSignTypedData();

    const [isDrawing, setIsDrawing] = useState(false);
    const [highlightedBox, setHighlightedBox] = useState<number | null>(null);
    const [winningBox, setWinningBox] = useState<number | null>(null);
    const [showPrizeCard, setShowPrizeCard] = useState(false);
    const [lastTxHash, setLastTxHash] = useState<string | null>(null);
    const [totalDrawsForPrizeCard, setTotalDrawsForPrizeCard] = useState(0);
    const [wonPrizes, setWonPrizes] = useState<
        Array<{
            id: number;
            name: string;
            amount?: string;
            quantity?: number;
            key?: string;
        }>
    >([]);

    const { todayStats } = luckyDrawStore;

    const { loading: contractLoading } = useLuckyDrawContract();

    const { currentRoundStats } = luckyDrawStore;
    const { pointsPerDraw } = currentRoundStats || {};

    const { data: walletClient } = useWalletClient();
    const contract = useContract(LUCKY_DRAW_CONTRACT_ADDRESS, LuckyDrawABI, true);
    const send = useSend();

    const handleQuantityChange = useCallback(
        (delta: number) => {
            const maxDraws = Math.min(MAX_DRAWS_PER_TIME, todayStats?.todayRemainingDraws || 0);
            const newQuantity = Math.max(0, Math.min(maxDraws, drawQuantity + delta));
            setDrawQuantity(newQuantity);
            setQuantityInputValue(newQuantity.toString());
        },
        [drawQuantity, todayStats?.todayRemainingDraws],
    );

    const handleQuantityInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;

            // Allow empty string for deletion
            if (value === '') {
                setQuantityInputValue('');
                setDrawQuantity(0);
                return;
            }

            // Only allow digits
            if (!/^\d+$/.test(value)) {
                return;
            }

            // Remove leading zeros and parse
            const numValue = parseInt(value, 10) || 0;
            const maxDraws = Math.min(MAX_DRAWS_PER_TIME, todayStats?.todayRemainingDraws || 0);
            const finalValue = Math.min(numValue, maxDraws);

            setDrawQuantity(finalValue);
            setQuantityInputValue(finalValue.toString());
        },
        [todayStats?.todayRemainingDraws],
    );

    const handleQuantityInputBlur = useCallback(() => {
        // If input is empty or invalid, set to 0
        if (quantityInputValue === '' || isNaN(parseInt(quantityInputValue, 10))) {
            setDrawQuantity(0);
            setQuantityInputValue('0');
        } else {
            // Ensure display value matches drawQuantity
            setQuantityInputValue(drawQuantity.toString());
        }
    }, [drawQuantity, quantityInputValue]);

    const getSnakePath = () => {
        return [0, 1, 2, 5, 4, 3, 6, 7, 8];
    };

    const processRewardIndices = (rewardIndices: number[]) => {
        if (rewardIndices.length === 0) return;

        const actualRewards = [];
        for (const index of rewardIndices) {
            try {
                const reward = luckyDrawStore.getRewardByIndex(index);
                if (reward) {
                    actualRewards.push({
                        index: Number(index),
                        name: reward.name,
                        amount: reward.amount,
                        key: reward.key,
                        rewardType: reward.name,
                        weight: Number(reward.weight),
                        decimal: reward.decimal,
                    });
                }
            } catch (error) {
                console.error('error:', error);
            }
        }

        if (actualRewards.length > 0) {
            const groupedRewards = actualRewards.reduce((acc, reward) => {
                const existingReward = acc.find((r) => r.index === reward.index);
                if (existingReward) {
                    existingReward.quantity = (existingReward.quantity || 1) + 1;
                } else {
                    acc.push({ ...reward, quantity: 1 });
                }
                return acc;
            }, [] as ((typeof actualRewards)[0] & { quantity: number })[]);

            setWonPrizes(
                groupedRewards.map((reward) => ({
                    id: reward.index,
                    name: reward.name,
                    amount: reward.amount,
                    quantity: reward.quantity,
                    decimal: reward.decimal,
                    key: reward.key,
                })),
            );
            refreshUserData();
        }
    };

    const refreshUserData = async () => {
        if (!user.address) return;
        try {
            await luckyDrawStore.refreshAllData(user.address, { page: 1, limit: 10 });
        } catch (error) {
            console.error('error:', error);
        }
    };

    useEffect(() => {
        luckyDrawStore.fetchCurrentRoundStats();
    }, []);

    const startDrawing = async () => {
        if (isDrawing || !walletClient || !user.address || !contract) return;

        try {
            setIsDrawing(true);
            setHighlightedBox(null);
            setWinningBox(null);
            setShowPrizeCard(false);

            const chainId = getCurrentChainId();
            const domain = {
                name: 'One Piece Lucky Draw',
                version: '1',
                chainId: chainId,
                verifyingContract: LUCKY_DRAW_CONTRACT_ADDRESS as `0x${string}`,
            };

            const types = {
                LuckyDraw: [
                    { name: 'address', type: 'address' },
                    { name: 'drawCount', type: 'uint256' },
                    { name: 'date', type: 'string' },
                    { name: 'costPoints', type: 'uint256' },
                    { name: 'issuedAt', type: 'uint256' },
                ],
            };

            const costPoints = (BigInt(drawQuantity) * BigInt(new BigNumber(pointsPerDraw || 0).toString())).toString();

            const value = {
                address: user.address,
                drawCount: drawQuantity,
                date: new Date().toISOString().split('T')[0],
                costPoints: costPoints,
                issuedAt: Math.floor(Date.now() / 1000),
            };

            const signature = await signTypedDataAsync({
                domain,
                types,
                primaryType: 'LuckyDraw',
                message: value,
            });

            const prepareDrawValue: PrepareDrawValue = {
                costPoints: value.costPoints,
                drawCount: value.drawCount,
                address: value.address,
                date: value.date,
                issuedAt: value.issuedAt,
            };

            const result = await getOnePieceServerApi(true).luckyDrawPreparation({
                address: user.address,
                signature: signature,
                value: prepareDrawValue,
            });

            setWonPrizes([]);

            const drawParams = [
                ethers.zeroPadValue(ethers.hexlify(result.luckyDrawPreparation.historyId), 32),
                drawQuantity,
                result.luckyDrawPreparation.expiresAt,
                result.luckyDrawPreparation.signature,
            ];

            let transactionSuccess = false;

            await send({
                contract,
                method: 'draw',
                params: drawParams,
                pendingText: 'drawing...',
                summary: 'draw',
                showSuccess: false,
                isLuckyDraw: true,
                onSuccess: async ({ res, hash }) => {
                    transactionSuccess = true;
                    try {
                        setLastTxHash(hash);
                        setTotalDrawsForPrizeCard(drawQuantity); // Save draw quantity before reset
                        await getOnePieceServerApi().luckyDrawSaveTxHash({
                            address: user.address,
                            historyId: result.luckyDrawPreparation.historyId,
                            txHash: hash,
                        });

                        await refreshUserData();
                    } catch (error) {
                        console.error('save transaction hash failed:', error);
                    }

                    let actualRewardIndices: number[] = [];
                    if (Array.isArray(res)) {
                        actualRewardIndices = res.map((idx: any) => Number(idx));
                    } else if (res && typeof res === 'object' && 'logs' in res) {
                        if (res.status !== 1) {
                            throw new Error('transaction failed');
                        }
                        if (res.logs && res.logs.length > 0) {
                            const drawnEvent = res.logs.find((log: any) => {
                                try {
                                    const parsed = contract.interface.parseLog(log);
                                    return parsed?.name === 'Drawn';
                                } catch (error) {
                                    return false;
                                }
                            });

                            if (drawnEvent) {
                                const parsed = contract.interface.parseLog(drawnEvent);
                                if (parsed) {
                                    actualRewardIndices = parsed.args.rewardIndices.map((idx: any) => Number(idx));
                                    if (actualRewardIndices.length > 0) {
                                        processRewardIndices(actualRewardIndices);
                                    }
                                }
                            }
                        }
                    }

                    setIsDrawing(false);
                    setDrawQuantity(0);
                    setQuantityInputValue('0');
                },
                onError: async () => {
                    setIsDrawing(false);
                    setQuantityInputValue('0');
                    setDrawQuantity(0);
                    // Refresh user data for all errors (including user rejection)
                    // because luckyDrawPreparation already deducted the draw count
                    try {
                        await refreshUserData();
                    } catch (refreshError) {
                        console.error('Failed to refresh user data:', refreshError);
                    }
                },
            });

            // Only continue with animation if transaction was successful
            if (!transactionSuccess) {
                return;
            }

            const snakePath = getSnakePath();
            const winningBoxIndex = Math.floor(Math.random() * 9);

            const totalDuration = 2000;
            const startTime = Date.now();
            let currentStep = 0;

            const animate = () => {
                const elapsed = Date.now() - startTime;
                const progress = Math.min(elapsed / totalDuration, 1);

                if (progress < 1) {
                    let speed;
                    if (progress < 0.8) {
                        speed = 0.05;
                    } else {
                        const slowProgress = (progress - 0.8) / 0.2;
                        speed = 0.05 + slowProgress * 0.1;
                    }

                    currentStep += speed;
                    const currentIndex = Math.floor(currentStep) % snakePath.length;

                    if (currentStep >= 1) {
                        setHighlightedBox(snakePath[currentIndex]);
                    }

                    requestAnimationFrame(animate);
                } else {
                    setHighlightedBox(winningBoxIndex);
                    setWinningBox(winningBoxIndex);
                    setIsDrawing(false);

                    setTimeout(() => {
                        setShowPrizeCard(true);
                    }, 1000);
                }
            };
            requestAnimationFrame(animate);
        } catch (error) {
            setIsDrawing(false);
            console.log(error);
        }
    };

    const closePrizeCard = () => {
        setShowPrizeCard(false);
        setWinningBox(null);
        setHighlightedBox(null);
    };

    const calculateCountdown = () => {
        const now = new Date();
        const utcToday = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 0, 0, 0, 0));
        const nextUtcMidnight = new Date(utcToday.getTime() + 24 * 60 * 60 * 1000);
        const timeDiff = nextUtcMidnight.getTime() - now.getTime();

        if (timeDiff <= 0) {
            return '00:00:00';
        }

        const hours = Math.floor(timeDiff / (1000 * 60 * 60));
        const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);

        const formatTime = (num: number) => num.toString().padStart(2, '0');
        return `${formatTime(hours)}:${formatTime(minutes)}:${formatTime(seconds)}`;
    };

    useEffect(() => {
        setCountdown(calculateCountdown());
        const interval = setInterval(() => {
            setCountdown(calculateCountdown());
        }, 1000);

        return () => {
            clearInterval(interval);
        };
    }, []);

    if (contractLoading) {
        return (
            <LuckyDrawPoolContainer id="lucky-draw-pool">
                <Header>
                    <TYPE.title fontSize={24}>Lucky Draw Pool</TYPE.title>
                    <RefreshButton>Lucky Draw box refresh every 24h</RefreshButton>
                </Header>
                <div style={{ textAlign: 'center', padding: '40px', color: 'white' }}>
                    Loading prize data from contract...
                </div>
            </LuckyDrawPoolContainer>
        );
    }

    return (
        <LuckyDrawPoolContainer id="lucky-draw-pool">
            <Header>
                <TYPE.title fontSize={24}>Lucky Draw Pool</TYPE.title>
                <RefreshButton>Lucky Draw box refresh every 24h</RefreshButton>
            </Header>

            <MainContent>
                <LeftPanel>
                    <GiftGrid>
                        {Array.from({ length: 9 }, (_, index) => (
                            <GiftBox key={index}>
                                <InnerBox isHighlighted={highlightedBox === index} isWinning={winningBox === index}>
                                    <div className="gift-icon" />
                                </InnerBox>
                            </GiftBox>
                        ))}
                    </GiftGrid>
                </LeftPanel>

                <RightPanel>
                    <div style={{ display: 'flex', flexDirection: 'column', width: '200px', margin: '0 auto' }}>
                        <CountdownSection>
                            <CountdownTimer>{countdown}</CountdownTimer>
                            <EndTimeLabel>End Time</EndTimeLabel>
                        </CountdownSection>

                        <TabContainer>
                            <Tab isActive={activeTab === 'Points'} onClick={() => setActiveTab('Points')}>
                                Points
                            </Tab>
                            <Tab disabled>
                                NFT Pieces
                                <ComingSoonBadge>Coming Soon</ComingSoonBadge>
                            </Tab>
                        </TabContainer>

                        <DrawQuantityContainer>
                            <QuantityButton onClick={() => handleQuantityChange(-1)}>-</QuantityButton>
                            <QuantityDisplay
                                type="text"
                                value={quantityInputValue}
                                onChange={handleQuantityInputChange}
                                onBlur={handleQuantityInputBlur}
                                min="0"
                                max={Math.min(MAX_DRAWS_PER_TIME, todayStats?.todayRemainingDraws || 0)}
                            />
                            <QuantityButton onClick={() => handleQuantityChange(1)}>+</QuantityButton>
                        </DrawQuantityContainer>
                        <Row gap="5px" justify="flex-end" style={{ marginBottom: '20px', marginTop: '7px' }}>
                            <TYPE.desc fontSize={12}>Limit: 10 draws each time</TYPE.desc>
                        </Row>
                        <DrawInfo>
                            <DrawInfoRow>
                                <span>Points Cost</span>
                                <span>
                                    <span style={{ color: 'rgba(247, 147, 26, 1)' }}>{200 * drawQuantity}</span>
                                </span>
                            </DrawInfoRow>
                            <DrawInfoRow>
                                <Row gap="5px" align="center">
                                    <span>Remaining Today</span>
                                    <TooltipInfo title="The number of draws left for today." placement="top">
                                        <StyledErrorIcon />
                                    </TooltipInfo>
                                </Row>
                                <span>
                                    <span style={{ color: '#4CAF50' }}>{todayStats?.todayRemainingDraws}</span>
                                </span>
                            </DrawInfoRow>
                        </DrawInfo>
                        <StartDrawingButton
                            onClick={startDrawing}
                            disabled={
                                true ||
                                isDrawing ||
                                drawQuantity === 0 ||
                                drawQuantity > MAX_DRAWS_PER_TIME ||
                                !todayStats?.todayEligibility
                            }
                        >
                            {isDrawing ? 'Drawing...' : 'Start Drawing'}
                        </StartDrawingButton>

                        <ParticipantsSection>
                            <img src={AvatarIcon} alt="avatar" style={{ height: '24px' }} />
                            <ParticipantCount>
                                <MemberIcon />
                                <span>{currentRoundStats?.uniqueUsersCount || '--'}</span>
                            </ParticipantCount>
                        </ParticipantsSection>
                    </div>
                </RightPanel>
            </MainContent>

            {/* <PrizeProbabilityBar prizes={rewards} totalWeight={totalWeight} /> */}
            {/* 中奖卡片 */}
            <PrizeCard
                isVisible={showPrizeCard}
                prizes={wonPrizes}
                totalDraws={totalDrawsForPrizeCard}
                txHash={lastTxHash || ''}
                onClose={closePrizeCard}
            />
        </LuckyDrawPoolContainer>
    );
}

export default observer(LuckyDrawPool);
